import { Component, DestroyRef, ElementRef, computed, inject, signal, viewChild } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Observable, concat, forkJoin, of } from 'rxjs';import {
  YdsButtonComponent,
  YdsButtonBackComponent,
  YdsCardEditableComponent,
  YdsComboboxComponent,
  YdsDatePickerComponent,
  YdsDropdownComponent,
  YdsSpinnerComponent,
  YdsSwitchComponent,
  YdsTextAreaComponent,
  YdsTextFieldComponent,
  type YdsComboboxOption,
  type YdsDropdownOption,
} from '../../../yds-angular';
import {
  defaultEndDate,
  defaultStartDate,
  firstDayOfMonthFilter,
  firstOfMonthValidator,
  formatDisplayDate,
  isInvalid,
  lastDayOfMonthFilter,
  lastOfMonthValidator,
  startBeforeEndValidator,
  toFormDate,
} from '../../../shared/general-ledger-yds.utils';
import { BookingGroupService } from '../bookinggroup.service';
import { BookingCriterionService } from '../bookingcriterion.service';
import { BookingExcludedElementService } from '../bookingexcludedelement.service';
import { ConfirmDialogService } from '../../../dialog/confirm-dialog/confirm-dialog.service';
import { BookingGroup } from '../../../model/booking-group.model';
import { BookingCriterion } from '../../../model/booking-criterion.model';
import { BookingGroupExcludedElement } from '../../../model/booking-group-excluded-element.model';
import { Element } from '../../../model/element.model';
import { Element2 } from '../../../model/element2.model';
import { ElementService } from '../../bookingdefinition/element.service';
import { deleteBookingGroupWithChecks } from '../bookinggroup-shared';

const MAX_END_DATE_ISO = '9999-12-31';

/** Maximum number of criteria allowed in a booking group. */
const MAX_CRITERIA = 8;

@Component({
  selector: 'app-bookinggroup-editable',
  imports: [
    ReactiveFormsModule,
    FormsModule,
    YdsCardEditableComponent,
    YdsButtonComponent,
    YdsButtonBackComponent,
    YdsSpinnerComponent,
    YdsSwitchComponent,
    YdsComboboxComponent,
    YdsDropdownComponent,
    YdsDatePickerComponent,
    YdsTextAreaComponent,
    YdsTextFieldComponent,
  ],
  templateUrl: './bookinggroup-editable.component.html',
  styleUrl: './bookinggroup-editable.component.scss',
})
export class BookinggroupEditableComponent {
  readonly isInvalid = isInvalid;
  readonly firstDayOfMonthFilter = firstDayOfMonthFilter;
  readonly lastDayOfMonthFilter = lastDayOfMonthFilter;
  readonly translate = inject(TranslateService);

  private formBuilder = inject(FormBuilder);
  private bookingGroupService = inject(BookingGroupService);
  private bookingCriterionService = inject(BookingCriterionService);
  private bookingExcludedElementService = inject(BookingExcludedElementService);
  private elementService = inject(ElementService);
  private dialogService = inject(ConfirmDialogService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private destroyRef = inject(DestroyRef);
  private document = inject(DOCUMENT);

  private readonly detailsCard = viewChild('detailsCard', { read: ElementRef<HTMLElement> });
  private readonly criteriaTableBody = viewChild('criteriaTableBody', { read: ElementRef<HTMLElement> });

  /** Id of the booking group being edited, or null in add mode. */
  readonly bookingGroupId = signal<string | null>(null);
  readonly isLoading = signal(false);
  readonly isSaving = signal(false);
  readonly notFound = signal(false);

  /** The last loaded/saved record, used for the read-only view and to revert on cancel. */
  private readonly record = signal<BookingGroup | null>(null);

  readonly detailsEditing = signal(false);

  /** General ledger options for the "Grootboek" combobox (add mode). */
  readonly generalLedgerOptions = signal<YdsComboboxOption[]>([]);

  readonly form: FormGroup = this.formBuilder.group(
    {
      generalLedgerId: ['', Validators.required],
      bookingGroupName: ['', Validators.required],
      bookByWageType: [false],
      startDate: [defaultStartDate(), [Validators.required, firstOfMonthValidator()]],
      endDate: [null, lastOfMonthValidator()],
      memoValue: [''],
    },
    { validators: startBeforeEndValidator() },
  );

  /** Whether we are creating a new booking group (no id in the route). */
  readonly isAddMode = computed(() => this.bookingGroupId() === null);

  /** Display text for the "Grootboek" value in view mode and when editing an existing record. */
  readonly grootboekName = computed(() => {
    const current = this.record();
    if (current?.generalLedger_name) {
      return current.generalLedger_name;
    }
    const selectedId = this.form.get('generalLedgerId')?.value as string | null;
    return this.generalLedgerOptions().find((o) => o.id === selectedId)?.label ?? '';
  });

  /** Display text for the "Boekingsgroep" value in view mode. */
  readonly bookingGroupName = computed(() => this.record()?.bookingGroup_name ?? '');

  // --- Criteria card state ------------------------------------------------------

  readonly criteriaEditing = signal(false);
  readonly isSavingCriteria = signal(false);

  /** Rows shown in the Criteria grid (working copy, persisted on save). */
  readonly criteriaRows = signal<BookingCriterion[]>([]);

  /** Ids of existing criteria removed during edit, deleted on save. */
  private removedCriterionIds: string[] = [];

  /** Whether the save was attempted, used to surface required-field errors. */
  readonly criteriaValidationTriggered = signal(false);

  private readonly criterionElements = signal<Element[]>([]);

  /** Dropdown options showing "Rubric - element name", keyed by element id. */
  readonly criterionElementOptions = computed<YdsDropdownOption[]>(() =>
    this.criterionElements().map((e) => ({
      id: e.elementId,
      label: e.rubric ? `${e.rubric} - ${e.elementName}` : e.elementName,
    })),
  );

  /** Whether another criterion row may still be added (max {@link MAX_CRITERIA}). */
  readonly canAddCriterion = computed(() => this.criteriaRows().length < MAX_CRITERIA);

  readonly conversionTableOptions = signal<YdsComboboxOption[]>([]);

  // --- Niet signaleren card state -----------------------------------------------

  readonly excludedEditing = signal(false);
  readonly isSavingExcluded = signal(false);

  /** Rows shown in the "Niet signaleren" grid (working copy, persisted on save). */
  readonly excludedRows = signal<BookingGroupExcludedElement[]>([]);

  /** Ids of existing excluded elements removed during edit, deleted on save. */
  private removedExcludedIds: string[] = [];

  /** Whether the save was attempted, used to surface required-field errors. */
  readonly excludedValidationTriggered = signal(false);

  private readonly excludableElements = signal<Element[]>([]);

  /** All output code elements, used to resolve the code of already-excluded rows. */
  private readonly outputCodeElements = signal<Element2[]>([]);

  /**
   * Combobox options showing "outputCode - element name", keyed by element id.
   * Searchable on either part (the combobox filters on the label text).
   */
  readonly excludableElementOptions = computed<YdsComboboxOption[]>(() => {
    const options = this.excludableElements().map((e) => ({
      id: e.elementId,
      label: this.formatOutputCodeLabel(e.outputCode, e.elementName),
    }));
    const ids = new Set(options.map((o) => o.id));

    // Elements already excluded are not returned by getExcludableElements(), so add
    // the currently-selected rows to keep their label visible in the combobox.
    for (const row of this.excludedRows()) {
      if (row.elementId && !ids.has(row.elementId)) {
        ids.add(row.elementId);
        options.push({ id: row.elementId, label: this.excludedRowLabel(row) });
      }
    }

    return options;
  });

  constructor() {
    const id = this.route.snapshot.paramMap.get('id');

    if (id) {
      this.bookingGroupId.set(id);
      this.loadBookingGroup(id);
      this.loadCriteriaLookups();
      this.loadCriteria(id);
      this.loadExcludableElements();
      this.loadExcludedElements(id);
    } else {
      // Add mode: start directly in edit state and load the selectable general ledgers.
      this.detailsEditing.set(true);
      this.loadGeneralLedgers();
    }
  }

  private loadBookingGroup(id: string): void {
    this.isLoading.set(true);
    this.bookingGroupService
      .getBookingGroupById(id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (group) => {
          if (!group) {
            this.notFound.set(true);
          } else {
            this.record.set(group);
            this.patchForm(group);
          }
          this.isLoading.set(false);
        },
        error: () => {
          this.notFound.set(true);
          this.isLoading.set(false);
        },
      });
  }

  private loadGeneralLedgers(): void {
    this.bookingGroupService
      .getGeneralLedgersByContext()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((ledgers) => {
        this.generalLedgerOptions.set(ledgers.map((l) => ({ id: l.id, label: l.name })));
      });
  }

  private loadCriteriaLookups(): void {
    this.bookingCriterionService
      .getCriterionElements()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((elements) => this.criterionElements.set(elements));

    this.bookingGroupService
      .getConversionTables()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((tables) => {
        this.conversionTableOptions.set(tables.map((t) => ({ id: t.conversionTableId, label: t.name })));
      });
  }

  private loadCriteria(id: string): void {
    this.bookingCriterionService
      .getCriteriaByGroup(id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((rows) => this.criteriaRows.set(rows));
  }

  private loadExcludableElements(): void {
    this.bookingExcludedElementService
      .getExcludableElements()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((elements) => this.excludableElements.set(elements));

    this.elementService
      .getOutputCodeElements()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((elements) => this.outputCodeElements.set(elements));
  }

  private loadExcludedElements(id: string): void {
    this.bookingExcludedElementService
      .getExcludedElementsByGroup(id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((rows) => this.excludedRows.set(rows));
  }

  private patchForm(group: BookingGroup): void {
    this.form.patchValue({
      generalLedgerId: group.generalLedgerId,
      bookingGroupName: group.bookingGroup_name,
      bookByWageType: group.bookByWageType,
      startDate: toFormDate(group.startDate),
      endDate: this.isMaxEndDate(group.endDate) ? null : toFormDate(group.endDate),
      memoValue: group.memo_Value ?? '',
    });
    this.form.markAsPristine();
    this.form.markAsUntouched();
  }

  // --- View helpers -------------------------------------------------------------

  readonly startDateDisplay = computed(() =>
    formatDisplayDate(this.record()?.startDate, 'nl-NL'),
  );

  readonly endDateDisplay = computed(() => {
    const end = this.record()?.endDate;
    return this.isMaxEndDate(end) ? 'n.v.t.' : formatDisplayDate(end, 'nl-NL');
  });

  readonly bookByWageTypeDisplay = computed(() => (this.record()?.bookByWageType ? 'Ja' : 'Nee'));

  /** Display text for the "Memo" value in view mode. */
  readonly memoDisplay = computed(() => this.record()?.memo_Value || 'n.v.t.');

  /** Whether "Boeken per persoon" is enabled, used for the view-mode glyph/label. */
  readonly bookByWageTypeOn = computed(() => Boolean(this.record()?.bookByWageType));

  private isMaxEndDate(value: unknown): boolean {
    const date = toFormDate(value as Date | string | null);
    if (!date) {
      return false;
    }
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}` === MAX_END_DATE_ISO;
  }

  // --- Details card actions -----------------------------------------------------

  onDetailsCancel(): void {
    if (this.isAddMode()) {
      this.goBack();
      return;
    }

    const current = this.record();
    if (current) {
      this.patchForm(current);
    }
    this.detailsEditing.set(false);
    this.scrollToDetails();
  }

  onDetailsNext(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const value = this.form.value as {
      generalLedgerId: string;
      bookingGroupName: string;
      bookByWageType: boolean;
      startDate: Date | null;
      endDate: Date | null;
      memoValue: string;
    };

    const ledgerName = this.grootboekName();

    const payload: BookingGroup = {
      bookingGroupId: this.bookingGroupId() ?? '',
      generalLedgerId: value.generalLedgerId,
      generalLedger_name: ledgerName,
      bookingGroup_name: value.bookingGroupName,
      bookByWageType: value.bookByWageType,
      startDate: (value.startDate ?? defaultStartDate()) as Date,
      endDate: (value.endDate ?? defaultEndDate()) as Date,
      memo_Value: value.memoValue ?? '',
    };

    this.isSaving.set(true);

    const id = this.bookingGroupId();
    const save$ = id
      ? this.bookingGroupService.updateBookingGroup(id, payload)
      : this.bookingGroupService.addBookingGroup(payload);

    save$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (saved) => {
        this.isSaving.set(false);
        if (id) {
          this.record.set({ ...payload, ...saved });
          this.detailsEditing.set(false);
          this.scrollToDetails();
        } else if (saved?.bookingGroupId) {
          this.router.navigate(['/config/bookinggroup/editable', saved.bookingGroupId]);
        } else {
          this.router.navigate(['/config/bookinggroup']);
        }
      },
      error: () => {
        this.isSaving.set(false);
      },
    });
  }

  // --- Criteria card actions ----------------------------------------------------

  /** Returns the first unused number in the range 1–8 for a new criterion row. */
  private nextAvailableCriterionNumber(): number {
    const used = new Set(this.criteriaRows().map((row) => row.number));
    for (let n = 1; n <= MAX_CRITERIA; n++) {
      if (!used.has(n)) return n;
    }
    return MAX_CRITERIA; // fallback – canAddCriterion() prevents reaching this
  }

  addCriterionRow(): void {
    if (!this.canAddCriterion()) {
      return;
    }
    const newNumber = this.nextAvailableCriterionNumber();
    const newRow: BookingCriterion = {
      bookingCriterionId: null,
      bookingGroupId: this.bookingGroupId() ?? '',
      elementId: '',
      elementName: '',
      conversionTableId: null,
      conversionTableName: '',
      number: newNumber,
      kindOfCriterion: '-',
      startPosition: 0,
      numberOfPositions: 0,
      active: true,
      numeric: false,
      list: false,
    };
    this.criteriaRows.update((rows) =>
      [...rows, newRow].sort((a, b) => a.number - b.number),
    );
    setTimeout(() => {
      const tbody = this.criteriaTableBody()?.nativeElement;
      if (!tbody) return;
      const rows = tbody.querySelectorAll('tr');
      const insertedIndex = this.criteriaRows().findIndex((r) => r.number === newNumber);
      const targetRow = rows[insertedIndex] ?? rows[rows.length - 1];
      if (!targetRow) return;
      const top = targetRow.getBoundingClientRect().top + window.scrollY - 290;
      window.scrollTo({ top, behavior: 'smooth' });
    });
  }

  removeCriterionRow(index: number): void {
    const rows = this.criteriaRows();
    const row = rows[index];
    if (row?.bookingCriterionId) {
      this.removedCriterionIds.push(row.bookingCriterionId);
    }
    this.criteriaRows.set(rows.filter((_, i) => i !== index));
  }

  /** Dropdown options for a criterion row. */
  criterionElementOptionsForRow(): YdsDropdownOption[] {
    return this.criterionElementOptions();
  }

  /** Display label for a criterion row in view mode ("Rubric - element name" when known). */
  criterionRowLabel(row: BookingCriterion): string {
    return (
      this.criterionElementOptions().find((o) => o.id === row.elementId)?.label ??
      row.elementName ??
      row.elementId
    );
  }

  /** Combobox options for an excluded row, excluding codes already chosen in other rows. */
  excludableElementOptionsForRow(index: number): YdsComboboxOption[] {
    const rows = this.excludedRows();
    const ownId = rows[index]?.elementId;
    const usedIds = new Set(
      rows
        .filter((_, i) => i !== index)
        .map((row) => row.elementId)
        .filter(Boolean),
    );
    // Always keep the row's own selection so its label resolves (never the raw id).
    return this.excludableElementOptions().filter((o) => o.id === ownId || !usedIds.has(o.id));
  }

  onCriterionElementChange(index: number, elementId: string): void {
    const element = this.criterionElements().find((e) => e.elementId === elementId);
    this.criteriaRows.update((rows) =>
      rows.map((row, i) =>
        i === index
          ? {
              ...row,
              elementId,
              elementName: element?.elementName ?? '',
              numeric: element?.numeric ?? row.numeric,
              list: element?.list ?? row.list,
            }
          : row,
      ),
    );
  }

  onCriterionConversionTableChange(index: number, conversionTableId: string | null): void {
    const label =
      this.conversionTableOptions().find((o) => o.id === conversionTableId)?.label ?? '';
    this.criteriaRows.update((rows) =>
      rows.map((row, i) =>
        i === index
          ? { ...row, conversionTableId: conversionTableId || null, conversionTableName: label }
          : row,
      ),
    );
  }

  /** Resolves the conversion label for view mode, preferring the lookup by id. */
  conversionTableLabel(row: BookingCriterion): string {
    if (!row.conversionTableId) {
      return row.conversionTableName ?? '';
    }
    return (
      this.conversionTableOptions().find((o) => o.id === row.conversionTableId)?.label ??
      row.conversionTableName ??
      ''
    );
  }

  onCriterionNumberChange(
    index: number,
    field: 'startPosition' | 'numberOfPositions',
    value: number | null,
  ): void {
    this.criteriaRows.update((rows) =>
      rows.map((row, i) => (i === index ? { ...row, [field]: value ?? 0 } : row)),
    );
  }

  isCriterionRowInvalid(row: BookingCriterion): boolean {
    return this.criteriaValidationTriggered() && !row.elementId;
  }

  onCriteriaCancel(): void {
    this.removedCriterionIds = [];
    this.criteriaValidationTriggered.set(false);
    const id = this.bookingGroupId();
    if (id) {
      this.loadCriteria(id);
    }
    this.criteriaEditing.set(false);
  }

  onCriteriaSave(): void {
    if (this.criteriaRows().some((row) => !row.elementId)) {
      this.criteriaValidationTriggered.set(true);
      return;
    }

    const duplicateCombinations = this.getDuplicateCriteriaCombinations();
    if (duplicateCombinations.length > 0) {
      this.showDuplicateCriteriaDialog(duplicateCombinations);
      return;
    }

    const id = this.bookingGroupId();
    if (!id) {
      return;
    }

    this.isSavingCriteria.set(true);

    const deleteOperations: Observable<unknown>[] = [];
    const updateOperations: Observable<unknown>[] = [];
    const addOperations: Observable<unknown>[] = [];

    this.removedCriterionIds.forEach((criterionId) =>
      deleteOperations.push(this.bookingCriterionService.removeCriterion(criterionId)),
    );

    this.criteriaRows().forEach((row, index) => {
      const criterion: BookingCriterion = {
        ...row,
        bookingGroupId: id,
        number: row.number,
      };
      if (row.bookingCriterionId) {
        updateOperations.push(
          this.bookingCriterionService.updateCriterion(row.bookingCriterionId, criterion),
        );
      } else {
        addOperations.push(this.bookingCriterionService.addCriterion(criterion));
      }
    });

    const save$: Observable<unknown> = this.runInOrder(
      deleteOperations,
      updateOperations,
      addOperations,
    );

    save$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.isSavingCriteria.set(false);
        this.removedCriterionIds = [];
        this.criteriaValidationTriggered.set(false);
        this.criteriaEditing.set(false);
        this.loadCriteria(id);
      },
      error: () => {
        this.isSavingCriteria.set(false);
      },
    });
  }

  /** Returns duplicated unique-combination values shown in the Criteria card. */
  private getDuplicateCriteriaCombinations(): string[] {
    const rowsByKey = new Map<string, BookingCriterion[]>();

    for (const row of this.criteriaRows()) {
      const key = this.getCriterionUniquenessKey(row);
      const existingRows = rowsByKey.get(key) ?? [];
      existingRows.push(row);
      rowsByKey.set(key, existingRows);
    }

    return Array.from(rowsByKey.values())
      .filter((rows) => rows.length > 1)
      .map((rows) => this.formatCriterionCombination(rows[0]));
  }

  /** Builds the uniqueness key for: Criterium + Vanaf + Lengte + Conversielabel. */
  private getCriterionUniquenessKey(row: BookingCriterion): string {
    const startPosition = row.startPosition ?? 0;
    const numberOfPositions = row.numberOfPositions ?? 0;
    const conversionTableId = row.conversionTableId ?? '';
    return [row.elementId ?? '', String(startPosition), String(numberOfPositions), conversionTableId].join('|');
  }

  /** Formats one criteria combination for warning dialogs. */
  private formatCriterionCombination(row: BookingCriterion): string {
    const criterium = row.elementName || row.elementId || 'n.v.t.';
    const vanaf = row.startPosition ?? 0;
    const lengte = row.numberOfPositions ?? 0;
    const conversieLabel = this.conversionTableLabel(row) || 'n.v.t.';
    return `Criterium: ${criterium}, Vanaf: ${vanaf}, Lengte: ${lengte}, Conversielabel: ${conversieLabel}`;
  }

  /** Warns when duplicate criteria combinations are present and blocks save. */
  private showDuplicateCriteriaDialog(duplicateCombinations: string[]): void {
    this.dialogService.open({
      width: '520px',
      title: 'Opslaan niet mogelijk',
      message:
        "De combinatie 'Criterium', 'Vanaf', 'Lengte' en 'Conversielabel' moet uniek zijn.\n\n" +
        `Dubbele gegevens:\n- ${duplicateCombinations.join('\n- ')}`,
      confirmText: 'OK',
      variant: 'warning',
    });
  }

  // --- Niet signaleren card actions ---------------------------------------------

  /** Adds an empty row so the placeholder is shown until the user picks an uitvoercode. */
  addExcludedRow(): void {
    const newRow: BookingGroupExcludedElement = {
      bookingGroupExcludedElementId: null,
      bookingGroupId: this.bookingGroupId() ?? '',
      elementId: '',
      elementName: '',
      sequence: -1,
      bookingExclude: 2,
      numeric: false,
      list: false,
    };
    this.excludedRows.update((rows) => [...rows, newRow]);
  }

  removeExcludedRow(index: number): void {
    const rows = this.excludedRows();
    const row = rows[index];
    if (row?.bookingGroupExcludedElementId) {
      this.removedExcludedIds.push(row.bookingGroupExcludedElementId);
    }
    this.excludedRows.set(rows.filter((_, i) => i !== index));
  }

  onExcludedElementChange(index: number, elementId: string): void {
    // Re-selecting the row's current code (e.g. after typing a search) keeps the row as is.
    if (!elementId || this.excludedRows()[index]?.elementId === elementId) {
      return;
    }
    // Codes already saved in this group are not returned by getExcludableElements(),
    // so fall back to the full output code list to resolve the name.
    const element =
      this.excludableElements().find((e) => e.elementId === elementId) ??
      this.outputCodeElements().find((e) => e.elementId === elementId);
    this.excludedRows.update((rows) =>
      rows.map((row, i) =>
        i === index
          ? {
              ...row,
              elementId,
              elementName: element?.elementName ?? '',
              numeric: element?.numeric ?? row.numeric,
              list: element?.list ?? row.list,
            }
          : row,
      ),
    );
  }

  /** Display label for an excluded row ("outputCode - element name" when known). */
  excludedRowLabel(row: BookingGroupExcludedElement): string {
    const outputCode =
      this.excludableElements().find((e) => e.elementId === row.elementId)?.outputCode ??
      this.outputCodeElements().find((e) => e.elementId === row.elementId)?.outputCode;
    return this.formatOutputCodeLabel(outputCode, row.elementName || row.elementId);
  }

  private formatOutputCodeLabel(outputCode: number | null | undefined, elementName: string): string {
    return outputCode != null ? `${outputCode} - ${elementName}` : elementName;
  }

  isExcludedRowInvalid(row: BookingGroupExcludedElement): boolean {
    return this.excludedValidationTriggered() && !row.elementId;
  }

  onExcludedCancel(): void {
    this.removedExcludedIds = [];
    this.excludedValidationTriggered.set(false);
    const id = this.bookingGroupId();
    if (id) {
      this.loadExcludedElements(id);
    }
    this.excludedEditing.set(false);
  }

  onExcludedSave(): void {
    if (this.excludedRows().some((row) => !row.elementId)) {
      this.excludedValidationTriggered.set(true);
      return;
    }

    const id = this.bookingGroupId();
    if (!id) {
      return;
    }

    this.isSavingExcluded.set(true);

    const deleteOperations: Observable<unknown>[] = [];
    const updateOperations: Observable<unknown>[] = [];
    const addOperations: Observable<unknown>[] = [];

    this.removedExcludedIds.forEach((excludedId) =>
      deleteOperations.push(this.bookingExcludedElementService.removeExcludedElement(excludedId)),
    );

    this.excludedRows().forEach((row) => {
      const excludedElement: BookingGroupExcludedElement = {
        ...row,
        bookingGroupId: id,
      };
      if (row.bookingGroupExcludedElementId) {
        updateOperations.push(
          this.bookingExcludedElementService.updateExcludedElement(
            row.bookingGroupExcludedElementId,
            excludedElement,
          ),
        );
      } else {
        addOperations.push(this.bookingExcludedElementService.addExcludedElement(excludedElement));
      }
    });

    const save$: Observable<unknown> = this.runInOrder(
      deleteOperations,
      updateOperations,
      addOperations,
    );

    save$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.isSavingExcluded.set(false);
        this.removedExcludedIds = [];
        this.excludedValidationTriggered.set(false);
        this.excludedEditing.set(false);
        this.loadExcludedElements(id);
      },
      error: () => {
        this.isSavingExcluded.set(false);
      },
    });
  }

  /**
   * Runs the given operation groups sequentially: all deletes first, then all
   * updates, and finally all adds. Operations within a group run in parallel.
   */
  private runInOrder(...groups: Observable<unknown>[][]): Observable<unknown> {
    const phases = groups
      .filter((group) => group.length > 0)
      .map((group) => forkJoin(group));
    return phases.length ? concat(...phases) : of(null);
  }

  // --- Page actions -------------------------------------------------------------

  private scrollToDetails(): void {
    setTimeout(() => {
      const element = this.detailsCard()?.nativeElement;
      if (!element) {
        return;
      }

      const chrome = this.document.querySelector('.app-chrome') as HTMLElement | null;
      const offset = chrome?.offsetHeight ?? 0;
      const top = element.getBoundingClientRect().top + window.scrollY - offset - 16;

      window.scrollTo({ top, behavior: 'smooth' });
    });
  }

  goBack(): void {
    this.router.navigateByUrl('config/bookinggroup');
  }

  goToBookingDefinition(): void {
    this.router.navigate(['/config/bookingdefinition', this.bookingGroupId()]);
  }

  goToBookingRule(): void {
    this.router.navigate(['/config/bookingrule', this.bookingGroupId()]);
  }

  onDelete(): void {
    const id = this.bookingGroupId();
    if (!id) {
      return;
    }

    deleteBookingGroupWithChecks({
      id,
      bookingGroupName: this.bookingGroupName(),
      service: this.bookingGroupService,
      dialogService: this.dialogService,
      destroyRef: this.destroyRef,
      onDeleted: () => this.router.navigateByUrl('config/bookinggroup'),
    });
  }
}
